import { Search, X } from "lucide-react";
import { forwardRef, useRef, type ForwardedRef } from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "./cargando";
import { IconButton } from "./icon-button";
import { Input, type InputProps } from "./input";

export interface InputBusquedaProps extends Omit<InputProps, "type" | "value" | "onChange"> {
  valor: string;
  onValorChange: (v: string) => void;
  /** Etiqueta accesible (no hay label visible). Si no hay `placeholder`, tambien se usa de placeholder. */
  etiqueta: string;
  cargando?: boolean;
  /** `plano`: sin borde, para la cabecera de un Dialog de busqueda (BuscadorGlobal, SelectorCliente). */
  apariencia?: "campo" | "plano";
  /** Va al contenedor (ancho, margen), no al `<input>`. */
  className?: string;
}

/** Enchufa el mismo nodo al ref externo y al interno. */
function asignar(nodo: HTMLInputElement | null, ref: ForwardedRef<HTMLInputElement>): void {
  if (typeof ref === "function") ref(nodo);
  else if (ref) ref.current = nodo;
}

/**
 * Campo de busqueda con lupa a la izquierda y, a la derecha, el spinner mientras
 * busca o la X para limpiar. `pl-9` (12 + 16 + 8) y `pr-11` (44, el boton) son
 * insets de icono que solo se escriben aca, nunca en el feature.
 *
 * `type="search"` da el teclado adecuado en el celular; la X propia de WebKit
 * se oculta porque ya hay una de 36px con `aria-label`.
 */
export const InputBusqueda = forwardRef<HTMLInputElement, InputBusquedaProps>(
  (
    {
      valor,
      onValorChange,
      etiqueta,
      cargando = false,
      apariencia = "campo",
      className,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const interno = useRef<HTMLInputElement | null>(null);

    function limpiar(): void {
      onValorChange("");
      // El foco vuelve al campo: quien limpia casi siempre quiere escribir otra cosa.
      interno.current?.focus();
    }

    return (
      <div className={cn("relative", className)}>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          ref={(nodo) => {
            interno.current = nodo;
            asignar(nodo, ref);
          }}
          type="search"
          inputMode="search"
          autoComplete="off"
          aria-label={etiqueta}
          placeholder={placeholder ?? etiqueta}
          value={valor}
          onChange={(e) => onValorChange(e.target.value)}
          className={cn(
            "pl-9 pr-11 [&::-webkit-search-cancel-button]:appearance-none",
            apariencia === "plano" && "border-0 bg-transparent focus-visible:ring-0",
          )}
          {...props}
        />
        {cargando || valor ? (
          /*
            Centrado vertical de verdad (`top-1/2` + `-translate-y-1/2`), no
            `top-0` con la altura del campo copiada a mano. Lo de antes andaba
            de casualidad: la caja media `size-11 sm:size-10` y el `Input` mide
            `h-11 sm:h-10`, asi que empataban. En el ticket de venta el campo
            se pisa a `h-12` y la X quedaba 4px arriba del centro. Asi se
            centra sola contra cualquier alto, igual que la lupa de al lado.
          */
          <span className="absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center sm:size-10">
            {cargando ? (
              <Spinner className="text-muted-foreground" />
            ) : (
              <IconButton icono={X} aria-label="Limpiar" tamano="sm" onClick={limpiar} />
            )}
          </span>
        ) : null}
      </div>
    );
  },
);
InputBusqueda.displayName = "InputBusqueda";
